import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Button, Stack } from "@mui/material";
import { FufiTransactionRow, HeadingS, HomepageTransactionsList, NumberToCZK, useTypedTranslation } from "@repo/ui";
import { Link, navigate } from "gatsby";
import { isEmpty, isNil } from "lodash";

import {
    LifeTransactionEnum,
    PensionTransactionEnum,
    ProductType,
    type GetLifeTransactionsResponse,
    type GetPensionTransactionsResponse,
} from "@repo/api";
import { useFufiTransactionsMoneyIns, type SupportedContractType } from "@repo/logic";
import { useMemo } from "react";
import { contractLink } from "../../functions";
import { isUnsupportedFufiContractTypeError, useTransactions } from "../../queryHooks";
import { TransactionsListSkeleton } from "../DPS/Transactions/TransactionsList";
import { TransactionsContainer, type TransactionsContainerProps } from "../Transactions/TransactionsContainer";
import { TransactionsNotSupported } from "../ZivotniPojisteni/Tabs/components/Transactions/TransasctionComponent";

const TRANSACTIONS_PAGE_SIZE = 4;

/**
 * Komponenta podporuje loading stav ktery se ukazuje, dokud nenacte transakce
 * k idcku. Transakce nezacne nacitat, dokud nedostane idcko
 */
type ITransactionsById = {
    id: string;
    type: SupportedContractType;
    isLoading?: boolean;
    transactionMapper?: (
        data: GetPensionTransactionsResponse | GetLifeTransactionsResponse,
    ) => GetPensionTransactionsResponse | GetLifeTransactionsResponse;
};

const transactionLinkByType: Record<SupportedContractType, string> = {
    [ProductType.Cl]: "pohyby",
    [ProductType.Clf]: "pohyby",
    [ProductType.Uf]: "pohyby-platby",
    [ProductType.Pf]: "pohyby-platby",
};

const lifeTypeToTransactionTypeMap: Record<
    LifeTransactionEnum | PensionTransactionEnum,
    TransactionsContainerProps["transactions"][number]["type"]
> = {
    [LifeTransactionEnum.LifeEmployer]: "CONTRIB_EMPLOYER",
    [LifeTransactionEnum.LifeInsurer]: "LIFE_INSURER",
    [LifeTransactionEnum.LifePayIn]: "LIFE_PAY_IN",
    [LifeTransactionEnum.LifePayOut]: "LIFE_PAY_OUT",
    [LifeTransactionEnum.Other]: "CONTRIB_CLIENT",
    [PensionTransactionEnum.ContribClient]: "CONTRIB_CLIENT",
    [PensionTransactionEnum.ContribEmployer]: "CONTRIB_EMPLOYER",
    [PensionTransactionEnum.ContribState]: "CONTRIB_STATE",
};

const TransactionsById = ({ id, type, transactionMapper, isLoading = false }: ITransactionsById) => {
    const { t } = useTypedTranslation();

    //Query pro vsechny typy krom Fufi
    const {
        data: transactionData,
        isSuccess: transactionIsSuccess,
        isError: transactionIsError,
    } = useTransactions({
        type,
        params: {
            contractId: id,
            count: TRANSACTIONS_PAGE_SIZE,
        },
        options: {
            enabled: Boolean(id) && type !== ProductType.Clf,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
                if (failureCount >= 3) return false;
                return !isUnsupportedFufiContractTypeError(error, type);
            },
            throwOnError: (error) => {
                return !isUnsupportedFufiContractTypeError(error, type);
            },
            select: (data) => {
                if (!transactionMapper || isNil(data)) return data;
                return data.map((transaction) => transactionMapper(transaction)) as
                    | GetLifeTransactionsResponse[]
                    | GetPensionTransactionsResponse[];
            },
        },
    });
    //Query pro Fufi
    const {
        flatData: fufiData,
        query: { isSuccess: fufiIsSuccess, isError: fufiIsError },
    } = useFufiTransactionsMoneyIns({
        contractId: id,
        moneyInsPageSize: TRANSACTIONS_PAGE_SIZE,
        options: {
            enabled: !isEmpty(id) && type === ProductType.Clf,
        },
    });

    const mappedData: TransactionsContainerProps["transactions"] = useMemo(() => {
        if (type !== ProductType.Clf && !isNil(transactionData)) {
            return transactionData.map((transaction) => ({
                type: lifeTypeToTransactionTypeMap[transaction.type],
                amount: transaction.amount,
                date: transaction.date,
                title: transaction.title,
            }));
        }

        return [];
    }, [type, transactionData]);

    const isSuccess = type === ProductType.Clf ? fufiIsSuccess : transactionIsSuccess;
    const isError = type === ProductType.Clf ? fufiIsError : transactionIsError;

    const homepageRows = useMemo(
        () =>
            mappedData.map((transaction, index) => ({
                key: `${transaction.date}-${transaction.amount}-${index}`,
                title: transaction.title,
                dateLabel: transaction.date ? new Date(transaction.date).toLocaleDateString("cs-CZ") : "-",
                amountLabel: NumberToCZK(transaction.amount),
            })),
        [mappedData],
    );

    if (type !== ProductType.Clf) {
        return (
            <HomepageTransactionsList
                heading={t(`fufi.transactions.lastPayments`)}
                isLoading={!isSuccess || isLoading}
                isError={isError}
                isSuccess={isSuccess}
                emptyMessage={t(`global.contractDetailUnavailable`)}
                rows={homepageRows}
                showAllTo={contractLink(id, type, transactionLinkByType[type])}
                showAllLabel={t(`fufi.transactions.showAllPayments`)}
                linkComponent={Link}
            />
        );
    }

    return (
        <Stack sx={{ gap: 4 }}>
            <HeadingS mb={0}>{t(`fufi.transactions.lastPayments`)}</HeadingS>
            {(() => {
                if (isError) return <TransactionsNotSupported />;
                if (isSuccess && !isLoading) {
                    if (type === ProductType.Clf && fufiData.length > 0) {
                        return (
                            <Stack gap={4}>
                                {fufiData.slice(0, TRANSACTIONS_PAGE_SIZE).map((payment) => (
                                    <FufiTransactionRow key={payment.id} payment={payment} />
                                ))}
                            </Stack>
                        );
                    }
                    return <TransactionsContainer transactions={mappedData} isIcon={true} />;
                }
                return <TransactionsListSkeleton skeletonsCount={4} />;
            })()}
            <Button
                sx={{ width: "fit-content", pl: 0 }}
                disabled={!isSuccess}
                onClick={() => {
                    navigate(contractLink(id, type, transactionLinkByType[type]));
                }}
                variant="text"
                size="small"
                endIcon={<ArrowForwardIcon />}
            >
                {t(`fufi.transactions.showAllPayments`)}
            </Button>
        </Stack>
    );
};

export default TransactionsById;
